import React from 'react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import {
  Landmark,
  Star,
  Pencil,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Expand,
  PlayCircle,
  Plus,
  Shrink,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { ConfirmModal } from '../../../common/ConfirmModal';
import { getLoggedUser } from '../../../../state/user';
import { getPaymentAccount, updatePaymentAccount, actionPaymentAccount } from './services/bankAccountServices';
import PaymentBankAccountModal from './components/PaymentBankAccountModal';
import '../../../../styles/components/bank-account-cards.scss';
import BankAccountCardSkeleton from './components/BankAccountCardSkeleton';
import BankAccountsEmpty from './components/BankAccountsEmpty';

const user = getLoggedUser();

// NOTE: this assumes each account row carries a `verification_status`
// field ('verified' | 'pending') in addition to the existing `is_active` /
// `is_default` flags. Adjust the key below if your API names it differently.

class PaymentBankAccount extends React.Component {
  constructor() {
    super();
    this.state = {
      accounts: [],
      isLoading: false,

      // create / update modal
      showFormModal: false,
      selectedAccount: null,

      // set-as-primary confirm
      showPrimaryConfirm: false,
      primaryTarget: null,
      primaryLoading: false,

      // activate / deactivate confirm
      showActionConfirm: false,
      actionTarget: null,
      actionLoading: false,

      hideSidebar: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchAccounts();
  }

  fetchAccounts = async () => {
    this.setState({ isLoading: true });

    try {
      const response = await getPaymentAccount();

      if (response.success) {
        this.setState({ accounts: response.data || [] });
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  /* ----------------------------------------------------------------------
   * Create / Update — both handled by PaymentBankAccountModal
   * -------------------------------------------------------------------- */

  handleCreate = () => {
    this.setState({ selectedAccount: null, showFormModal: true });
  };

  handleEdit = (row) => {
    this.setState({ selectedAccount: row, showFormModal: true });
  };

  handleFormModalClose = () => {
    this.setState({ showFormModal: false, selectedAccount: null });
  };

  handleSaved = () => {
    this.setState({ showFormModal: false, selectedAccount: null });
    this.fetchAccounts();
  };

  /* ----------------------------------------------------------------------
   * Set as primary — reuses the update endpoint, sending the row's
   * existing fields with is_default forced to true.
   * -------------------------------------------------------------------- */

  handleSetPrimaryClick = (row) => {
    this.setState({ primaryTarget: row, showPrimaryConfirm: true });
  };

  handleSetPrimaryConfirm = async () => {
    const { primaryTarget } = this.state;
    if (!primaryTarget) return;

    this.setState({ primaryLoading: true });

    try {
      const payload = {
        bank_name: primaryTarget.bank_name,
        branch_name: primaryTarget.branch_name,
        account_name: primaryTarget.account_name,
        account_number: primaryTarget.account_number,
        iban: primaryTarget.iban,
        swift_code: primaryTarget.swift_code,
        currency: primaryTarget.currency,
        remarks: primaryTarget.remarks,
        is_default: true,
      };

      const response = await updatePaymentAccount(primaryTarget.id, payload);

      if (response.success) {
        toast.success(response.message || i18n.t('primary_account_updated'));
        this.setState({ showPrimaryConfirm: false, primaryTarget: null });
        this.fetchAccounts();
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ primaryLoading: false });
    }
  };

  /* ----------------------------------------------------------------------
   * Activate / deactivate
   * -------------------------------------------------------------------- */

  handleActionClick = (row) => {
    this.setState({ actionTarget: row, showActionConfirm: true });
  };

  handleActionConfirm = async () => {
    const { actionTarget } = this.state;
    if (!actionTarget) return;

    const action = actionTarget.is_active ? 'deactivate' : 'activate';
    const payload = { action: action };

    this.setState({ actionLoading: true });

    try {
      const response = await actionPaymentAccount(actionTarget.id, payload);

      if (response.success) {
        toast.success(response.message || i18n.t(`account_${action}d`));
        this.setState({ showActionConfirm: false, actionTarget: null });
        this.fetchAccounts();
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  maskNumber = (value) => {
    if (!value) return '--';
    const str = String(value);
    return str.length > 4 ? `****${str.slice(-4)}` : str;
  };

  renderVerificationBadge = (row) => {
    const isVerified = row.verification_status ? row.verification_status === 'verified' : Boolean(row.is_verified);

    return isVerified ? (
      <span className="bank-account-card__status bank-account-card__status--verified">
        <CheckCircle2 size={14} />
        {i18n.t('verified')}
      </span>
    ) : (
      <span className="bank-account-card__status bank-account-card__status--pending">
        <Clock size={14} />
        {i18n.t('pending_verification')}
      </span>
    );
  };

  renderCard = (row) => {
    const isPrimary = row.is_default;

    return (
      <div key={row.id} className={`bank-account-card${isPrimary ? ' bank-account-card--primary' : ''}`}>
        {isPrimary && (
          <div className="bank-account-card__ribbon">
            <Star size={13} fill="#fff" />
            {i18n.t('primary')}
          </div>
        )}

        <div className="bank-account-card__body">
          <div className="bank-account-card__identity">
            <span className="bank-account-card__icon">
              <Landmark size={20} />
            </span>
            <div>
              <p className="bank-account-card__name">{row.bank_name}</p>
              <p className="bank-account-card__meta">
                {[row.branch_name, row.currency].filter(Boolean).join(' · ') || '--'}
              </p>
            </div>
          </div>

          <div className="bank-account-card__details">
            <div className="bank-account-card__row">
              <span className="bank-account-card__label">{i18n.t('account_name')}</span>
              <span className="bank-account-card__value">{this.maskNumber(row.account_name)}</span>
            </div>
            <div className="bank-account-card__row">
              <span className="bank-account-card__label">{i18n.t('account_number')}</span>
              <span className="bank-account-card__value">{this.maskNumber(row.account_number)}</span>
            </div>
            <div className="bank-account-card__row">
              <span className="bank-account-card__label">{i18n.t('iban')}</span>
              <span className="bank-account-card__value">{row.iban || '--'}</span>
            </div>
            <div className="bank-account-card__row">
              <span className="bank-account-card__label">{i18n.t('swift_code')}</span>
              <span className="bank-account-card__value">{row.swift_code || '--'}</span>
            </div>
          </div>

          {/* {this.renderVerificationBadge(row)} */}

          <span
            className={`bank-account-card__status bank-account-card__status--${row.is_active ? 'active' : 'inactive'}`}
            style={{ marginLeft: '0.5rem' }}
          >
            {row.is_active ? i18n.t('active') : i18n.t('inactive')}
          </span>
        </div>

        <div className="bank-account-card__footer">
          <button type="button" className="bank-account-card__action" onClick={() => this.handleEdit(row)}>
            <Pencil size={15} />
            {i18n.t('edit')}
          </button>

          {!isPrimary && (
            <button
              type="button"
              className="bank-account-card__action bank-account-card__action--primary"
              onClick={() => this.handleSetPrimaryClick(row)}
            >
              <Star size={15} />
              {i18n.t('set_as_primary_account')}
            </button>
          )}

          <button
            type="button"
            className="bank-account-card__action bank-account-card__action--danger"
            onClick={() => this.handleActionClick(row)}
          >
            {row.is_active ? <Trash2 size={15} /> : <PlayCircle size={15} />}
            {row.is_active ? i18n.t('delete') : i18n.t('activate')}
          </button>

          {/* <button
            type="button"
            className="bank-account-card__action bank-account-card__action--danger"
            onClick={() => this.handleActionClick(row)}
          >
            <Trash2 size={15} />
            {i18n.t('delete')}
          </button> */}
        </div>
      </div>
    );
  };

  render() {
    const {
      accounts,
      isLoading,
      hideSidebar,
      showFormModal,
      selectedAccount,
      showPrimaryConfirm,
      primaryTarget,
      primaryLoading,
      showActionConfirm,
      actionTarget,
      actionLoading,
    } = this.state;

    const canCreate = accounts.length < 4 || user?.role === 'A';

    return (
      <React.Fragment>
        <HeaderBanner title={i18n.t('bank_accounts')} subtitle={i18n.t('bank_accounts_subtitle')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar ? <Shrink strokeWidth={1} /> : <Expand strokeWidth={1} />}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="bank-accounts">
                      <div className="bank-accounts__header">
                        <div className="bank-accounts__header-info">
                          <span className="bank-accounts__header-icon">
                            <Landmark size={22} />
                          </span>
                          <div>
                            <h2 className="bank-accounts__header-title">{i18n.t('bank_accounts')}</h2>
                            <p className="bank-accounts__header-subtitle">{i18n.t('bank_accounts_subtitle')}</p>
                          </div>
                        </div>

                        {canCreate && (
                          <button className="bank-accounts__add-btn" onClick={this.handleCreate}>
                            <Plus size={18} />
                            <span>{i18n.t('add_bank_account')}</span>
                          </button>
                        )}
                      </div>

                      {isLoading ? (
                        <BankAccountCardSkeleton count={4} />
                      ) : accounts.length ? (
                        <div className="bank-accounts-grid">{accounts.map(this.renderCard)}</div>
                      ) : (
                        <BankAccountsEmpty
                          message={i18n.t('no_bank_accounts_available')}
                          actionText={i18n.t('refresh')}
                          onAction={this.fetchAccounts}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Create / Update modal */}
        <PaymentBankAccountModal
          show={showFormModal}
          account={selectedAccount}
          onClose={this.handleFormModalClose}
          onSaved={this.handleSaved}
        />

        {/* Set-as-primary confirmation */}
        <ConfirmModal
          show={showPrimaryConfirm}
          onClose={() => this.setState({ showPrimaryConfirm: false, primaryTarget: null })}
          title={i18n.t('set_as_primary_account')}
          titleIcon={<CheckCircle2 size={22} color="#198754" />}
          message={i18n.t('set_as_primary_confirm_message', {
            bank: primaryTarget?.bank_name,
          })}
          onConfirm={this.handleSetPrimaryConfirm}
          onCancel={() => this.setState({ showPrimaryConfirm: false, primaryTarget: null })}
          confirmText={i18n.t('confirm')}
          cancelText={i18n.t('cancel')}
          loading={primaryLoading}
        />

        {/* Activate / deactivate confirmation */}
        <ConfirmModal
          show={showActionConfirm}
          onClose={() => this.setState({ showActionConfirm: false, actionTarget: null })}
          title={actionTarget?.is_active ? i18n.t('deactivate_account') : i18n.t('activate_account')}
          titleIcon={<AlertTriangle size={22} color="#dc3545" />}
          message={
            actionTarget?.is_active
              ? i18n.t('deactivate_account_confirm_message')
              : i18n.t('activate_account_confirm_message')
          }
          onConfirm={this.handleActionConfirm}
          onCancel={() => this.setState({ showActionConfirm: false, actionTarget: null })}
          confirmText={actionTarget?.is_active ? i18n.t('delete') : i18n.t('activate')}
          cancelText={i18n.t('cancel')}
          loading={actionLoading}
        />
      </React.Fragment>
    );
  }
}

export default PaymentBankAccount;
